import { apiFetch } from "./client";

/**
 * Client360 Этап 6 «Профіль» (type-aware surface) — client.
 *
 * Read:  GET  /Custom/frontend/client360/:holdingId/profile
 * Verify lifecycle: POST .../profile/{submit|withdraw|verify|return|mark-stale}
 * Suggestion (§8):  POST .../profile-suggestions/:id/{accept|reject}
 *
 * SURFACE over 3.0A-4 BusinessProfile: scenario/JSON editing is DELEGATED to the
 * Quick Form (clientRoutes.businessProfile) — never duplicated here.
 */

export type ProfileVerifyState = "empty" | "draft" | "needs_review" | "verified" | "stale";
export type ProfileSurfaceMode = "commercial" | "breeder" | "neutral" | "unclassified";

export interface ProfileVisibility {
  surfaceMode: ProfileSurfaceMode;
  neutral: boolean;
  showClassification: boolean;
  showBusinessProfile: boolean;
  showBreederCard: boolean;
  showVerifyBlock: boolean;
  showLegalForm: boolean;
  requiredByType: string[];
  breederHidden: string[];
  neutralMessage: string | null;
}

export interface ProfileClassification {
  recordType: string;
  personType: string;
  legalForm: string | null;
  primaryBusinessModel: string | null;
  businessModels: string[];
  additionalActivities: string[];
}

export interface ProfileBusinessProfile {
  scenario: string | null;
  fields: {
    businessScale: string | null;
    decisionModel: string | null;
    purchasingModel: string | null;
    orderFrequency: string | null;
    potentialEstimate: string | null;
    competitorBrands: string[];
    assortmentFocus: string[];
    competitorNote: string | null;
    nextCommercialAction: string | null;
  };
  scenarioAnswers: Array<{ key: string; value: unknown }>;
  modules: string[];
}

export interface ProfileBreeder {
  animalTypes: string[];
  breeds: string[];
  countBand: string | null;
  kennelName: string | null;
  program: string | null;
  feedingProgram: string | null;
  littersSummary: string | null;
  purchaseHistory: { amount365: number; source: string; readOnly: boolean } | null;
}

export interface ProfileVerify {
  state: ProfileVerifyState;
  status: string | null;
  submittedAt: string | null;
  submittedBy: string | null;
  reviewedAt: string | null;
  reviewedBy: string | null;
}

export interface ProfileStale {
  stale: boolean;
  reason: string | null;
  at: string | null;
}

export interface ProfileSuggestion {
  id: string;
  fieldCode: string;
  fieldLabel: string;
  suggestedDisplay: string;
  currentDisplay: string;
  message: string;
  sourceMeetingId: string | null;
  createdAt: string;
}

export interface ProfileAllowedActions {
  canOpenProfile: boolean;
  canOpenEditor: boolean;
  canSubmit: boolean;
  canWithdraw: boolean;
  canVerify: boolean;
  canReturn: boolean;
  canMarkStale: boolean;
  canResolveSuggestion: boolean;
}

export interface Client360Profile {
  holdingId: string;
  profileId: string | null;
  versionNumber: number | null;
  recordType: string;
  classified: boolean;
  visibility: ProfileVisibility;
  classification: ProfileClassification | null;
  businessProfile: ProfileBusinessProfile | null;
  breeder: ProfileBreeder | null;
  verify: ProfileVerify;
  stale: ProfileStale;
  suggestions: ProfileSuggestion[];
  allowedActions: ProfileAllowedActions;
  meta: { updatedAt: string };
}

export function fetchClient360Profile(
  holdingId: string,
  signal?: AbortSignal,
): Promise<Client360Profile> {
  return apiFetch<Client360Profile>(
    `/Custom/frontend/client360/${encodeURIComponent(holdingId)}/profile`,
    { signal },
  );
}

export type ProfileTransition = "submit" | "withdraw" | "verify" | "return" | "mark-stale";

export function profileTransition(
  holdingId: string,
  transition: ProfileTransition,
  body: { profileId: string; versionNumber: number; reason?: string },
): Promise<{ ok: boolean; transition: string; versionNumber: number; status: string }> {
  return apiFetch(
    `/Custom/frontend/client360/${encodeURIComponent(holdingId)}/profile/${transition}`,
    { method: "POST", body },
  );
}

export function resolveSuggestion(
  suggestionId: string,
  action: "accept" | "reject",
): Promise<{ ok: boolean; action: string; downgraded: boolean }> {
  return apiFetch(
    `/Custom/frontend/client360/profile-suggestions/${encodeURIComponent(suggestionId)}/${action}`,
    { method: "POST", body: {} },
  );
}
